import React, { useMemo } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { PensionFundDetails, PensionFundType } from "../../../interfaces/DTO/mislaka_xml_pension_models";
import { formatPensionFundType } from "../../../utils/enumFormatter";
import { Box, Typography } from "@mui/material";
import StyledDataGrid from "../../../shared/StyledDataGrid";
import { currencyColumn, percentageColumn } from "../../../shared/UIParts/columnsFormatter";

interface PensionFundDetailsTableProps {
  data: PensionFundDetails[];
}

const PensionFundDetailsTable: React.FC<PensionFundDetailsTableProps> = ({
  data,
}) => {
  // הכנת השורות עם הטקסט המפורמט
  const rows = useMemo(() => {
    // ספירת כמה שורות עם PensionFundType.PAYING
    const payingCount = data.filter(row => row.fund_type === PensionFundType.PAYING).length;
    const hasElevenPayingRows = payingCount === 11 || payingCount === 8;

    return data.map((row, index) => {
      const formattedFundType = formatPensionFundType(row.fund_type);
      const shouldShowCheck = hasElevenPayingRows && index === 7;
      const fundTypeDisplay = shouldShowCheck
        ? `${formattedFundType} ✓`
        : formattedFundType;

      return {
        id: index,
        ...row,
        fund_type_display: fundTypeDisplay,
        shouldShowCheck: shouldShowCheck
      };
    });
  }, [data]);

  // בדיקה האם יש עמודת גיל בדאטה (at_age)
  const hasAgeColumn = useMemo(() => {
    return data.some(row => row.at_age !== undefined && row.at_age !== null);
  }, [data]);

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [];

    // אם יש at_age בדאטה, נוסיף עמודת גיל כעמודה ראשונה
    if (hasAgeColumn) {
      baseColumns.push({
        field: "at_age",
        headerName: "גיל",
        width: 80,
        type: "number",
      });
    }

    // שאר העמודות
    baseColumns.push(
      {
        field: "fund_type_display",
        headerName: "סוג קופה",
        width: 150,
        renderCell: (params: any) => {
          return (
            <span style={{
              color: params.row.shouldShowCheck ? "green" : "inherit",
              fontWeight: params.row.shouldShowCheck ? "bold" : "normal"
            }}>
              {params.value}
            </span>
          );
        }
      },
      currencyColumn("retirement_pension_amount", "סכום קצבת זקנה עם הפקדות נוספות", 200),
      currencyColumn("expected_monthly_pension", "קצבת זקנה חודשית ללא הפקדות נוספות", 220),
      percentageColumn("forecast_yield_percentage", "אחוז תשואה בתחזית", 150),
      currencyColumn("total_expected_balance_with_premiums", "יתרה צפויה כולל פרמיות", 200),
      currencyColumn("expected_savings_without_premiums", "צבירת חיסכון ללא פרמיות", 200),
      currencyColumn("total_accumulated_amount_with_premiums", "סכום מצטבר כולל פרמיות", 200),
      currencyColumn("total_accumulated_amount_without_premiums", "סכום מצטבר ללא פרמיות", 250)
    );

    return baseColumns;
  }, [hasAgeColumn]);

  return (
    <Box dir="rtl" sx={{}}>
      <Typography variant="h5" gutterBottom>
        פרטי פוליסה פנסיונית
      </Typography>

      <StyledDataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[10, 25, 50]}
      />
    </Box>
  );
};

export default PensionFundDetailsTable;
